import { ArrowDownTrayIcon, ArrowUpTrayIcon, ClockIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  clearAccountingAccounts,
  createAccountingAccount,
  deleteAccountingAccount,
  downloadAccountingAccounts,
  getAccountingAccount,
  importAccountingAccounts,
  importAccountingAccountsFromQuickBooks,
  importGeneralLedgerTransactions,
  updateAccountingAccount,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Tooltip } from 'stories/components'
import { confirm } from 'utils'

import { AccountTransactions } from '../AccountTransactions'
import { ReconcileHistory } from '../ReconcileHistory'
import { EditAccountDialog } from './EditAccountDialog'
import {
  type IAccountingAccount,
  AccountingNormalBalanceOptions,
  AccountingStatementOptions,
  getAccountTypeLabel,
  resolveAccountGroup,
} from './types'

const UncategorizedSection = 'Uncategorized'

const sortAccounts = (accounts: IAccountingAccount[]) =>
  [...accounts].sort((a, b) => {
    const noDiff = (a.no || 0) - (b.no || 0)
    if (noDiff !== 0) return noDiff
    return String(a.number || '').localeCompare(String(b.number || ''))
  })

export const Accounts = () => {
  const [isLoading, setLoading] = useState(false)
  const [curEditItem, setCurEditItem] = useState<IAccountingAccount | null>()
  const [data, setData] = useState<IAccountingAccount[]>([])
  const [showTransaction, setShowTransaction] = useState(false)
  const [showReconcileHistory, setShowReconcileHistory] = useState(false)
  const [selectedAccountNo, setSelectedAccountNo] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const quickBooksFileInputRef = useRef<HTMLInputElement>(null)
  const glFileInputRef = useRef<HTMLInputElement>(null)

  const { hasPermission } = usePermissions()
  const canDelete = hasPermission('Accounting.Account.Delete')
  const canImportQuickbook = hasPermission('Accounting.Account.Import.Quickbooks')

  const sectionOptions = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNT_SECTIONS] || [])

  const accountsBySection = useMemo(() => {
    const configuredSections = sectionOptions
      .filter((option: { visible?: boolean; value?: string | null }) => option.visible !== false && option.value)
      .map((option: { value: string }) => option.value)

    const grouped = new Map<string, IAccountingAccount[]>()
    const ensureSection = (section: string) => {
      if (!grouped.has(section)) grouped.set(section, [])
    }

    configuredSections.forEach((section: string) => ensureSection(section))
    ensureSection(UncategorizedSection)

    data.forEach((account) => {
      const section = resolveAccountGroup(account.section, account.type, account.statement)
      ensureSection(section)
      grouped.get(section)!.push(account)
    })

    const orderedSections = [
      ...configuredSections,
      ...Array.from(grouped.keys()).filter(
        (section) => !configuredSections.includes(section) && section !== UncategorizedSection,
      ),
    ]
    if ((grouped.get(UncategorizedSection) || []).length > 0) {
      orderedSections.push(UncategorizedSection)
    }

    return orderedSections
      .filter((section) => (grouped.get(section) || []).length > 0)
      .map((section) => ({
        section,
        accounts: sortAccounts(grouped.get(section) || []),
      }))
  }, [data, sectionOptions])

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    const data = await getAccountingAccount()
    setData(data)
    setLoading(false)
  }

  const onUpdateEditItem = async (item: IAccountingAccount | null) => {
    setCurEditItem(undefined)
    if (!item) return

    const newData = cloneDeep(data)
    setLoading(true)
    if (item.id < 0) {
      const res = await createAccountingAccount(item)
      if (!res.success) {
      } else newData.push(res.data)
    } else {
      const index = data.findIndex((v) => v.id == item.id)
      const res = await updateAccountingAccount(item.id, item)
      if (!res.success) {
      } else {
        newData[index] = item
      }
    }
    setLoading(false)
    setData(newData)
  }

  const onDelete = async (item: IAccountingAccount) => {
    const index = data.findIndex((v) => v.id == item.id)
    if (index == -1) return

    if (item.id < 0) {
      const newValues = cloneDeep(data)
      newValues.splice(index, 1)
      setData(newValues)
      return
    }

    const result = await confirm(`Are you sure want to delete this account?\nAccount Name: ${item.name}`)
    if (!result) return
    setLoading(true)
    try {
      await deleteAccountingAccount(item.id)

      const newValues = cloneDeep(data)
      newValues.splice(index, 1)
      setData(newValues)
    } catch (e) {}
    setLoading(false)
  }

  const onClear = async () => {
    if (!canDelete) return
    const result = await confirm('Are you sure want to clear all accounts?')
    if (!result) return
    setLoading(true)
    try {
      await clearAccountingAccounts()
      setData([])
    } catch (e) {}
    setLoading(false)
  }

  const onDownload = async () => {
    setLoading(true)
    try {
      await downloadAccountingAccounts()
    } catch (e) {
      console.error('Download error:', e)
    }
    setLoading(false)
  }

  // const onImportClick = () => {
  //   fileInputRef.current?.click()
  // }

  const onImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const result = await confirm(
      'Are you sure you want to import accounts from this file? This will replace all existing accounts.',
    )
    if (!result) {
      event.target.value = ''
      return
    }

    setLoading(true)
    try {
      const response = await importAccountingAccounts(file)
      if (response.success) {
        alert(`Successfully imported ${response.imported} accounts`)
        await fetch()
      } else {
        alert(`Import failed:\n${response.errors.join('\n')}`)
      }
    } catch (e: any) {
      console.error('Import error:', e)
      alert(`Import error: ${e.message || 'Unknown error'}`)
    }
    setLoading(false)
    event.target.value = ''
  }

  const onImportQuickBooksClick = () => {
    quickBooksFileInputRef.current?.click()
  }

  const onImportQuickBooksFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const result = await confirm(
      'Are you sure you want to import accounts from QuickBooks? This will replace all existing accounts.',
    )
    if (!result) {
      event.target.value = ''
      return
    }

    setLoading(true)
    try {
      const response = await importAccountingAccountsFromQuickBooks(file)
      if (response.success) {
        alert(
          `Successfully imported ${response.imported} accounts${
            response.errors?.length ? `\n\nWarnings:\n${response.errors.join('\n')}` : ''
          }`,
        )
        await fetch()
      } else {
        alert(`Import failed:\n${response.errors?.join('\n') || 'Unknown error'}`)
      }
    } catch (e: any) {
      console.error('Import error:', e)
      alert(`Import error: ${e.message || 'Unknown error'}`)
    }
    setLoading(false)
    event.target.value = ''
  }

  const onImportGLClick = () => {
    glFileInputRef.current?.click()
  }

  const onImportGLFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const result = await confirm(
      'Are you sure you want to import transactions from this General Ledger file? Transactions will be added to matching accounts.',
    )
    if (!result) {
      event.target.value = ''
      return
    }

    setLoading(true)
    try {
      const response = await importGeneralLedgerTransactions(file)
      if (response.success) {
        const summaryLines = Object.entries(response.accountSummary || {})
          .map(([accNo, count]) => `  Account ${accNo}: ${count} transactions`)
          .join('\n')
        alert(
          `Successfully imported ${response.imported} transactions.\n\n${summaryLines}${
            response.errors?.length ? `\n\nWarnings:\n${response.errors.join('\n')}` : ''
          }`,
        )
      } else {
        alert(`Import failed:\n${response.errors?.join('\n') || 'Unknown error'}`)
      }
    } catch (e: any) {
      console.error('GL Import error:', e)
      alert(`Import error: ${e.message || 'Unknown error'}`)
    }
    setLoading(false)
    event.target.value = ''
  }

  if (showReconcileHistory) {
    return (
      <ReconcileHistory
        onBack={() => {
          setShowReconcileHistory(false)
        }}
      />
    )
  }

  if (showTransaction && selectedAccountNo) {
    return (
      <AccountTransactions
        accountNo={selectedAccountNo}
        onBack={() => {
          setShowTransaction(false)
          setSelectedAccountNo(null)
        }}
      />
    )
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center pb-5">
          Chart Of Accounts
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
        <div className="flex gap-2 items-center">
          <span className="hover:cursor-pointer text-shade-blue" onClick={onDownload}>
            <Tooltip message="Download">
              <ArrowDownTrayIcon className="w-4 h-4" />
            </Tooltip>
          </span>
          <span className="hover:cursor-pointer text-shade-blue" onClick={() => setShowReconcileHistory(true)}>
            <Tooltip message="Reconcile History">
              <ClockIcon className="w-4 h-4" />
            </Tooltip>
          </span>
          {canImportQuickbook && (
            <span className="hover:cursor-pointer text-shade-blue" onClick={onImportQuickBooksClick}>
              <Tooltip message="Import from QuickBooks">
                <ArrowUpTrayIcon className="w-4 h-4" />
              </Tooltip>
            </span>
          )}
          {canImportQuickbook && (
            <span className="hover:cursor-pointer text-shade-blue" onClick={onImportGLClick}>
              <Tooltip message="Import General Ledger Transactions">
                <ArrowUpTrayIcon className="w-4 h-4" />
              </Tooltip>
            </span>
          )}
          {canDelete && (
            <Button color="red" onClick={onClear} className="">
              Clear
            </Button>
          )}
          <Button onClick={() => setCurEditItem(null)} className="">
            Add
          </Button>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={onImportFile} style={{ display: 'none' }} />
      <input
        ref={quickBooksFileInputRef}
        type="file"
        accept=".xlsx"
        onChange={onImportQuickBooksFile}
        style={{ display: 'none' }}
      />
      <input
        ref={glFileInputRef}
        type="file"
        accept=".xlsx,.csv"
        onChange={onImportGLFile}
        style={{ display: 'none' }}
      />

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />

        <div className="sm:rounded-lg mb-8">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-3 py-3">
                  Acct #
                </th>
                <th scope="col" className="px-3 py-3">
                  Account Name
                </th>
                <th scope="col" className="px-3 py-3">
                  Type
                </th>
                <th scope="col" className="px-3 py-3">
                  Normal Balance
                </th>
                <th scope="col" className="px-3 py-3">
                  Statement
                </th>
                <th scope="col" className="px-3 py-3">
                  Subledger?
                </th>
                <th scope="col" className="px-3 py-3">
                  Notes
                </th>
                <th scope="col" className="px-3 py-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {accountsBySection.map(({ section, accounts }) => {
                const sectionLabel = section === UncategorizedSection ? section : `— ${section.toUpperCase()} —`

                return [
                  <tr className="border-b bg-blue-100 text-blue-900" key={`section-${section}`}>
                    <td className="px-3 py-2 font-semibold uppercase text-xs tracking-wide text-left" colSpan={8}>
                      {sectionLabel}
                    </td>
                  </tr>,
                  ...accounts.map((item, index) => (
                    <tr
                      className={`${
                        item.invalid ? 'bg-red-100' : index % 2 ? 'bg-slate-50' : 'bg-white'
                      } border-b dark:bg-gray-800 dark:border-gray-700`}
                      key={item.id}
                    >
                      <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {item.number || ''}
                      </td>

                      <td
                        scope="row"
                        className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap capitalize"
                      >
                        {item.name}
                      </td>

                      <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {getAccountTypeLabel(item.type)}
                      </td>

                      <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {item.normalBalance
                          ? AccountingNormalBalanceOptions[
                              item.normalBalance as keyof typeof AccountingNormalBalanceOptions
                            ] || item.normalBalance
                          : ''}
                      </td>

                      <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {item.statement
                          ? AccountingStatementOptions[item.statement as keyof typeof AccountingStatementOptions] ||
                            item.statement
                          : ''}
                      </td>

                      <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {item.parentId ? 'Yes' : 'No'}
                      </td>

                      <td
                        scope="row"
                        className="px-3 py-1 font-medium text-gray-900 dark:text-white max-w-xs truncate"
                        title={item.notes || ''}
                      >
                        {item.notes || ''}
                      </td>

                      <td className="px-6 py-1">
                        <div className="flex gap-2 items-center">
                          <span
                            className="text-green-600 hover:cursor-pointer hover:underline"
                            onClick={() => {
                              setShowTransaction(true)
                              setSelectedAccountNo(item.no)
                            }}
                          >
                            View Register
                          </span>
                          <span
                            className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                            onClick={() => setCurEditItem(item)}
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>

                          {canDelete && (
                            <span
                              className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                              onClick={() => onDelete(item)}
                            >
                              <TrashIcon className="w-4 h-4"></TrashIcon>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )),
                ]
              })}
            </tbody>
          </table>
        </div>

        {curEditItem !== undefined && (
          <EditAccountDialog item={curEditItem} onClose={onUpdateEditItem} allAccounts={data} />
        )}
      </div>
    </div>
  )
}
